import {
  Grid,
  Card,
  Divider,
  Tooltip,
  ToastProvider,
} from '@enterprise-ui/canvas-ui-react'
import { SyntheticEvent, useEffect, useState } from 'react'
import './Admin.scss'
import { UserAvatar } from '../Users/UserAvatar'
import { FlowLink } from '../Flows/FlowLink'
import { graphql, usePaginationFragment } from 'react-relay'
import { AdminDiscussionCardPaginationQuery } from './__generated__/AdminDiscussionCardPaginationQuery.graphql'
import { AdminDiscussionCardPaginationFragment_adminDashboardFlowComments$key } from './__generated__/AdminDiscussionCardPaginationFragment_adminDashboardFlowComments.graphql'
import { useSubscriptionRefreshQuery } from '../../hooks/useRefresh'
import { usePaginateDataLength } from '../../hooks/usePaginateDataLength'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { useGwtMutation } from '../../hooks'
import { AdminDiscussionCardMutation } from './__generated__/AdminDiscussionCardMutation.graphql'
import { AdminDiscussionCardMutation$variables } from './__generated__/AdminDiscussionCardMutation.graphql'
import { useAtom } from 'jotai'
import {
  deletedCommentFlowIdAtom,
  deletedCommentIdAtom,
  globalNotificationAtom,
} from '../../state/atoms'
import { getNotificationInputObject } from '../../util/relayUtils'
import { getTimeFromCreatedAtString } from '../../util/dateUtils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TimedSpinButton } from '../../GenericComponents/TimedSpinButton/TimedSpinButton'
import {
  AdminDiscussionCardUndoDeleteMutation,
  AdminDiscussionCardUndoDeleteMutation$variables,
} from './__generated__/AdminDiscussionCardUndoDeleteMutation.graphql'
import classNames from 'classnames'

export const AdminDiscussionCard = () => {
  const { data, loadNext, hasNext, isLoadingNext } =
    useAdminDiscussionCardQuery()

  const scrollEvent = (e: SyntheticEvent) => {
    if (isLoadingNext || !hasNext) {
      return
    }
    const target = e.target as HTMLTextAreaElement
    const scrollLeft = target.scrollHeight - target.scrollTop
    if (scrollLeft / target.clientHeight < 2) {
      loadNext(20)
    }
  }

  const notificationCount = data?.adminDashboardFlowComments?.edges.filter(
    (comment) => comment.node.comment?.unread,
  ).length

  const deleteComment = useDeleteAdminDiscussionCardFlowComment()

  return (
    <Card className="cardLayout">
      <div className="cardHeader flex hc-pa-lg">
        <Grid.Container spacing="none" align="center" className="flex-grow">
          <Grid.Item>
            <div className="card-header-text">Discussion</div>
          </Grid.Item>
          <Grid.Item className="hc-ml-sm">
            {!!notificationCount && (
              <div className="comment-notification-badge">
                {notificationCount}
              </div>
            )}
          </Grid.Item>
        </Grid.Container>
      </div>
      <Divider />
      <div onScroll={scrollEvent} className="comments flex-grow">
        {data &&
          data.adminDashboardFlowComments?.edges.map((page) => {
            const flow_comment = page.node
            return (
              <div key={flow_comment.comment?.rowId}>
                <div
                  className={classNames(
                    'hc-pt-lg',
                    'hc-pl-lg',
                    'hc-pb-lg',
                    'hc-pr-lg',
                    {
                      'comment-notification-border':
                        flow_comment.comment?.unread,
                    },
                  )}
                >
                  <Grid.Container spacing="none">
                    <Grid.Item xs={1}>
                      <UserAvatar
                        userRef={flow_comment.comment?.gwtUser}
                        showName={false}
                        aria-label={''}
                        children={undefined}
                      />
                    </Grid.Item>
                    <Grid.Item className="comment-container" xs={9}>
                      <div className="hc-ml-xs">
                        <span className="user-name-text">
                          {flow_comment.comment?.gwtUser?.fullName}
                        </span>
                        <span className="sub-text hc-ml-xs">
                          {getTimeFromCreatedAtString(
                            flow_comment.comment?.createdAt,
                          )}
                        </span>

                        {
                          <FlowLink isComment flowRef={flow_comment.flow}>
                            <p className="newline comment-hover dashboard-comment">
                              {flow_comment.comment?.commentText}
                            </p>
                          </FlowLink>
                        }
                      </div>
                    </Grid.Item>
                    <Grid.Item
                      xs={2}
                      className="self-center flex-grow align-right"
                    >
                      <Tooltip content="Remove comment" location="left">
                        <TimedSpinButton
                          iconOnly
                          type="default"
                          size="dense"
                          aria-label="remove-comment"
                          onClick={() => {
                            deleteComment(
                              flow_comment.comment?.rowId,
                              flow_comment.flow?.rowId,
                            )
                          }}
                          className="comment-delete-button"
                        >
                          <FontAwesomeIcon icon={faTimes} color="grey" />
                        </TimedSpinButton>
                      </Tooltip>
                    </Grid.Item>
                  </Grid.Container>
                </div>
                <Divider />
              </div>
            )
          })}
      </div>
    </Card>
  )
}

export const useAdminDiscussionCardQuery = () => {
  const [dataLength, setDataLength] = useState(20)

  const pageData =
    useSubscriptionRefreshQuery<AdminDiscussionCardPaginationQuery>(
      graphql`
        query AdminDiscussionCardPaginationQuery($count: Int!) {
          ...AdminDiscussionCardPaginationFragment_adminDashboardFlowComments
            @arguments(count: $count)
        }
      `,
      { count: dataLength || 20 },
      {
        component: 'ADMIN_DISCUSSION_CARD',
        uniqueComponentId: '',
        onNotification: [
          'ADMIN_DASHBOARD_FLOW_COMMENT_NOTIFICATION',
          'ADMIN_DASHBOARD_FLOW_COMMENT_DELETE_NOTIFICATION',
          'ADMIN_DASHBOARD_UNDO_FLOW_COMMENT_DELETE_NOTIFICATION',
          'LAST_SEEN_COMMENT_UPDATED_NOTIFICATION',
        ],
      },
    )

  const paginationFragment = usePaginationFragment<
    AdminDiscussionCardPaginationQuery,
    AdminDiscussionCardPaginationFragment_adminDashboardFlowComments$key
  >(
    graphql`
      fragment AdminDiscussionCardPaginationFragment_adminDashboardFlowComments on Query
      @argumentDefinitions(count: { type: "Int" }, cursor: { type: "Cursor" })
      @refetchable(
        queryName: "AdminDiscussionCardFlowCommentsPaginationQuery"
      ) {
        adminDashboardFlowComments(
          orderBy: UNREAD_COMMENTS_CREATED_AT_DESC
          first: $count
          after: $cursor
        )
          @connection(
            key: "AdminDiscussionCardPaginationFragment_adminDashboardFlowComments"
          ) {
          edges {
            node {
              comment {
                createdAt
                unread
                commentText
                rowId
                gwtUser {
                  fullName
                  ...UserAvatar_gwtUser
                }
              }
              flow {
                rowId
                name
                ...FlowLink_flow
              }
            }
          }
        }
      }
    `,
    pageData,
  )

  const paginateLength = usePaginateDataLength(
    paginationFragment?.data?.adminDashboardFlowComments?.edges,
    20,
  )

  useEffect(() => {
    setDataLength(paginateLength)
  }, [paginateLength])

  return { ...paginationFragment, pageData }
}

const useDeleteAdminDiscussionCardFlowComment = () => {
  const [, setRecentDeletedCommentId] = useAtom(deletedCommentIdAtom)
  const [, setRecentDeletedCommentFlowId] = useAtom(deletedCommentFlowIdAtom)
  const makeToast = ToastProvider.useToaster()
  const [, publishNotification] = useAtom(globalNotificationAtom)
  const { undoDeleteComment } = useUndoReviewerDiscussionCardDelete()

  const { commit } = useGwtMutation<AdminDiscussionCardMutation>(graphql`
    mutation AdminDiscussionCardMutation(
      $flowId: UUID!
      $commentId: UUID!
      $notificationInput: NotificationInput!
    ) {
      createNotification(input: { notification: $notificationInput }) {
        clientMutationId
      }
      deleteAdminDashboardFlowComment(
        input: { flowId: $flowId, commentId: $commentId }
      ) {
        clientMutationId
        deletedAdminDashboardFlowCommentId
      }
    }
  `)

  // Handles the deletion of a comment in the Discussion Board
  return (commentId: string | undefined, moduleFlowId: string | undefined) => {
    const mutation: AdminDiscussionCardMutation$variables =
      {} as AdminDiscussionCardMutation$variables
    mutation.commentId = commentId
    mutation.flowId = moduleFlowId
    mutation.notificationInput = getNotificationInputObject(
      'ADMIN_DASHBOARD_FLOW_COMMENT_DELETE_NOTIFICATION',
    )
    commit(
      mutation,
      () => {
        makeToast({
          autoHideDuration: 10000,
          type: 'success',
          heading: 'Comment deleted',
          message: 'Comment was successfully deleted.',
          links: [
            {
              text: 'undo',
              onClick: () => undoDeleteComment(commentId, moduleFlowId),
            },
          ],
        })
        publishNotification({
          id: 'local',
          gwt_user_id: 'local',
          name: 'ADMIN_DASHBOARD_FLOW_COMMENT_DELETE_NOTIFICATION',
          data: null,
        })
      },
      () => {
        makeToast({
          type: 'error',
          heading: 'Comment delete failed',
          message: 'Comment was unsuccessfully deleted.',
        })
      },
    )
    setRecentDeletedCommentFlowId(`${moduleFlowId}`)
    setRecentDeletedCommentId(`${commentId}`)
  }
}

const useUndoReviewerDiscussionCardDelete = () => {
  const [, publishNotification] = useAtom(globalNotificationAtom)
  const makeToast = ToastProvider.useToaster()

  const { commit } = useGwtMutation<AdminDiscussionCardUndoDeleteMutation>(
    graphql`
      mutation AdminDiscussionCardUndoDeleteMutation(
        $flowId: UUID!
        $commentId: UUID!
        $notificationInput: NotificationInput!
      ) {
        createNotification(input: { notification: $notificationInput }) {
          clientMutationId
        }
        createAdminDashboardFlowComment(
          input: {
            adminDashboardFlowComment: {
              flowId: $flowId
              commentId: $commentId
            }
          }
        ) {
          clientMutationId
        }
      }
    `,
  )

  const undoDeleteComment = (
    commentId: string | undefined,
    moduleFlowId: string | undefined,
  ) => {
    const mutation: AdminDiscussionCardUndoDeleteMutation$variables =
      {} as AdminDiscussionCardUndoDeleteMutation$variables
    mutation.commentId = commentId
    mutation.flowId = moduleFlowId
    mutation.notificationInput = getNotificationInputObject(
      'ADMIN_DASHBOARD_UNDO_FLOW_COMMENT_DELETE_NOTIFICATION',
    )
    commit(
      mutation,
      () => {
        publishNotification({
          id: 'local',
          gwt_user_id: 'local',
          name: 'ADMIN_DASHBOARD_UNDO_FLOW_COMMENT_DELETE_NOTIFICATION',
          data: null,
        })
      },
      () => {
        makeToast({
          autoHideDuration: 10000,
          type: 'error',
          heading: 'Comment delete failed.',
          message: 'Common was unsuccessfully deleted',
        })
      },
    )
  }

  return { undoDeleteComment }
}
